import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  HelpBanner,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@betstarters/ui'
import { Ban, Pencil, Plus, RotateCcw } from 'lucide-react'
import { api } from '../api'
import { useCapabilities } from '../hooks/useCapabilities'
import { messageOf } from '../lib/apiError'
import { Flag } from '../components/flag'
import {
  CatalogToolbar,
  ErrorBanner,
  PageHeading,
  Pagination,
  StatusBadge,
} from '../components/catalog'
import {
  CountryForm,
  emptyCountryForm,
  type CountryFormValue,
} from '../components/geoForms'

export interface Country {
  countryId: number
  country: string
  englishShortName: string
  isoCode2: string
  isoCode3: string
  isCompulsoryDocument: boolean
  numericCode: number | null
  isDisabledByPlatform: boolean
  creationDate: string
  lastModificationDate: string | null
}

interface CountryPage {
  countries: Country[]
  total: number
  offset: number
  limit: number
}

const PAGE_SIZE = 50

/**
 * CountriesPage is the geographic country catalogue —
 * [Catalog].[Geographic.Country] in the BsConnekto database.
 *
 * It anchors the whole Geo area: jurisdictions link to it, phone prefixes point
 * at it and the PSP hub keys its availability on it. None of those is a foreign
 * key in the database, so retiring a country here leaves every reference in
 * place — deliberately, since those rows are configuration.
 */
export function CountriesPage() {
  const { t } = useTranslation('app')
  const qc = useQueryClient()
  const { has } = useCapabilities()
  const canWrite = has('catalog:write')

  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [includeDisabled, setIncludeDisabled] = useState(true)
  const [offset, setOffset] = useState(0)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<Country | null>(null)
  const [form, setForm] = useState<CountryFormValue>(emptyCountryForm)
  const [error, setError] = useState('')

  const params = { search, includeDisabled: String(includeDisabled), offset, limit: PAGE_SIZE }

  const { data, isLoading } = useQuery({
    queryKey: ['countries', params],
    queryFn: async (): Promise<CountryPage> => {
      const res = await api.get('/countries', { params })
      return res.data as CountryPage
    },
  })

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['countries'] })
    // The pickers elsewhere read the same rows through /reference/countries.
    qc.invalidateQueries({ queryKey: ['reference'] })
  }

  const openCreate = () => {
    setEditing(null)
    setForm(emptyCountryForm)
    setError('')
    setDialogOpen(true)
  }

  const openEdit = (c: Country) => {
    setEditing(c)
    setForm({
      country: c.country,
      englishShortName: c.englishShortName,
      isoCode2: c.isoCode2,
      isoCode3: c.isoCode3,
      isCompulsoryDocument: c.isCompulsoryDocument,
      numericCode: c.numericCode === null ? '' : String(c.numericCode),
    })
    setError('')
    setDialogOpen(true)
  }

  const save = useMutation({
    mutationFn: () => {
      const body = {
        country: form.country.trim(),
        englishShortName: form.englishShortName.trim(),
        isoCode2: form.isoCode2.trim(),
        isoCode3: form.isoCode3.trim(),
        isCompulsoryDocument: form.isCompulsoryDocument,
        numericCode: form.numericCode === '' ? null : Number(form.numericCode),
        isDisabledByPlatform: editing?.isDisabledByPlatform ?? false,
      }
      return editing ? api.put(`/countries/${editing.countryId}`, body) : api.post('/countries', body)
    },
    onSuccess: () => {
      setDialogOpen(false)
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('countries.errors.save'))),
  })

  const toggle = useMutation({
    mutationFn: (c: Country) =>
      c.isDisabledByPlatform
        ? api.post(`/countries/${c.countryId}/restore`)
        : api.delete(`/countries/${c.countryId}`),
    onSuccess: () => {
      setError('')
      invalidate()
    },
    onError: (err) => setError(messageOf(err, t('countries.errors.save'))),
  })

  const countries = data?.countries ?? []
  const total = data?.total ?? 0

  return (
    <div>
      <PageHeading
        title={t('countries.title')}
        subtitle={t('countries.subtitle')}
        action={
          canWrite && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger
                render={
                  <Button onClick={openCreate}>
                    <Plus />
                    {t('countries.newCountry')}
                  </Button>
                }
              />
              <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                  <DialogTitle>
                    {editing ? t('countries.editCountry') : t('countries.newCountry')}
                  </DialogTitle>
                </DialogHeader>
                <CountryForm
                  value={form}
                  onChange={setForm}
                  onSubmit={() => save.mutate()}
                  onCancel={() => setDialogOpen(false)}
                  pending={save.isPending}
                  error={error}
                  submitLabel={editing ? t('common.save') : t('common.create')}
                />
              </DialogContent>
            </Dialog>
          )
        }
      />

      <HelpBanner storageKey="countries">
        <p>{t('countries.help.line1')}</p>
        <p>{t('countries.help.line2')}</p>
      </HelpBanner>

      <CatalogToolbar
        searchInput={searchInput}
        onSearchInput={setSearchInput}
        onSubmitSearch={() => {
          setSearch(searchInput)
          setOffset(0)
        }}
        searchPlaceholder={t('countries.searchPlaceholder')}
        includeDisabled={includeDisabled}
        onToggleDisabled={() => {
          setIncludeDisabled(!includeDisabled)
          setOffset(0)
        }}
        filtered={search !== '' || !includeDisabled}
        onClear={() => {
          setSearch('')
          setSearchInput('')
          setIncludeDisabled(true)
          setOffset(0)
        }}
      />

      {!dialogOpen && <ErrorBanner message={error} />}

      {isLoading ? (
        <p className="text-sm text-muted-foreground">{t('common.loading')}</p>
      ) : (
        <>
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  <TableHead className="w-16">{t('countries.columns.id')}</TableHead>
                  <TableHead>{t('countries.columns.name')}</TableHead>
                  <TableHead className="w-12" aria-label={t('countries.columns.flag')} />
                  <TableHead className="w-20">{t('countries.columns.iso2')}</TableHead>
                  <TableHead className="w-20">{t('countries.columns.iso3')}</TableHead>
                  <TableHead className="w-56">{t('countries.columns.document')}</TableHead>
                  <TableHead className="w-32">{t('common.columns.status')}</TableHead>
                  {canWrite && (
                    <TableHead className="w-28 text-right">{t('common.columns.actions')}</TableHead>
                  )}
                </TableRow>
              </TableHeader>
              <TableBody>
                {countries.map((c) => (
                  <TableRow key={c.countryId}>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {c.countryId}
                    </TableCell>
                    <TableCell className="font-medium">
                      <Link
                        to={`/countries/${c.countryId}`}
                        className="text-primary underline underline-offset-2"
                      >
                        {c.country}
                      </Link>
                      {c.englishShortName && c.englishShortName !== c.country && (
                        <span className="ml-2 text-xs font-normal text-muted-foreground">
                          {c.englishShortName}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <Flag region={c.isoCode2} />
                    </TableCell>
                    <TableCell className="font-mono text-xs">{c.isoCode2}</TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {c.isoCode3 || '—'}
                    </TableCell>
                    <TableCell>
                      {c.isCompulsoryDocument ? (
                        <Badge variant="outline">{t('countries.documentRequired')}</Badge>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusBadge disabled={c.isDisabledByPlatform} />
                    </TableCell>
                    {canWrite && (
                      <TableCell>
                        <div className="flex justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={t('common.edit')}
                            onClick={() => openEdit(c)}
                          >
                            <Pencil />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            title={c.isDisabledByPlatform ? t('common.restore') : t('common.retire')}
                            disabled={toggle.isPending}
                            onClick={() => toggle.mutate(c)}
                          >
                            {c.isDisabledByPlatform ? <RotateCcw /> : <Ban />}
                          </Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {countries.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={canWrite ? 7 : 6}
                      className="py-8 text-center text-muted-foreground"
                    >
                      {t('countries.empty')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>

          <Pagination
            total={total}
            offset={offset}
            pageSize={PAGE_SIZE}
            onOffset={setOffset}
            label={t('countries.total', { count: total })}
          />
        </>
      )}
    </div>
  )
}
